import { buildUnitReference } from "./propertyChildren";

export type VisitPropertyChild = {
  id?: number;
  floor?: string | null;
  apartment_number?: string | null;
  tower_block?: string | null;
  unit_reference?: string | null;
  is_rented?: boolean;
};

export type VisitPropertyParent = {
  id?: number | null;
  title?: string | null;
};

export type VisitPropertyFamily = {
  parentLabel: string | null;
  childrenCount: number;
  children: Array<{ id: number | null; label: string }>;
};

function trimText(value: unknown): string {
  if (value == null) return "";
  const text = String(value).trim();
  if (text === "" || text === "null" || text === "undefined") return "";
  return text;
}

export function formatVisitFloorLabel(floor: unknown): string {
  const text = trimText(floor);
  if (!text) return "";
  if (/^\d+$/.test(text)) return `${text}º`;
  return text;
}

function formatStreetLine(property: Record<string, unknown>): string {
  const street = trimText(property.address);
  const number = trimText(property.number);
  const neighborhood = trimText(property.neighborhood);

  let line = "";
  if (street && number) line = `${street}, ${number}`;
  else if (street) line = street;
  else if (number) line = number;

  if (line && neighborhood) return `${line} - ${neighborhood}`;
  if (neighborhood) return neighborhood;
  return line;
}

export function formatVisitPropertyCompactAddress(property: unknown): string {
  if (
    property == null ||
    Array.isArray(property) ||
    typeof property !== "object" ||
    Object.keys(property).length === 0
  ) {
    return "Endereço não disponível";
  }

  const record = property as Record<string, unknown>;
  const street = trimText(record.address);
  const number = trimText(record.number);
  const neighborhood = trimText(record.neighborhood);
  const apartmentNumber = trimText(record.apartment_number);
  const complement = trimText(record.complement);
  const streetAndNumber = [street, number].filter(Boolean).join(", ");
  const apartment = apartmentNumber
    ? /^apt(?:o|\.)?\s/i.test(apartmentNumber)
      ? apartmentNumber
      : `Apto ${apartmentNumber}`
    : complement;

  return (
    [streetAndNumber, neighborhood, apartment].filter(Boolean).join(" · ") ||
    "Endereço não disponível"
  );
}

export function formatVisitPropertyAddress(property: unknown): string {
  if (
    property == null ||
    Array.isArray(property) ||
    typeof property !== "object" ||
    Object.keys(property).length === 0
  ) {
    return "Endereço não disponível";
  }

  const record = property as Record<string, unknown>;
  const segments: string[] = [];
  const streetLine = formatStreetLine(record);
  if (streetLine) segments.push(streetLine);

  const complement = trimText(record.complement);
  if (complement) segments.push(`Complemento: ${complement}`);

  const floor = formatVisitFloorLabel(record.floor);
  if (floor) segments.push(`Andar: ${floor}`);

  const city = trimText(record.city);
  const uf = trimText(record.uf);
  const cityUf = [city, uf].filter(Boolean).join(" - ");
  if (cityUf) segments.push(cityUf);

  if (segments.length === 0) return "Endereço não disponível";
  return segments.join(" | ");
}

function formatParentLabel(parent: unknown, parentPropertyId: unknown): string | null {
  if (parent != null && typeof parent === "object" && !Array.isArray(parent)) {
    const record = parent as VisitPropertyParent;
    const title = trimText(record.title);
    const id = record.id != null && Number.isFinite(Number(record.id))
      ? Number(record.id)
      : null;
    if (title && id != null) return `${title} (#${id})`;
    if (title) return title;
    if (id != null) return `#${id}`;
  }

  if (parentPropertyId != null && Number.isFinite(Number(parentPropertyId))) {
    return `#${Number(parentPropertyId)}`;
  }

  return null;
}

function formatChildLabel(child: VisitPropertyChild): string {
  const reference = trimText(child.unit_reference);
  const floor = trimText(child.floor);
  const apartment = trimText(child.apartment_number);
  const tower = trimText(child.tower_block);

  let label = reference;
  if (!label && floor && apartment) {
    label = buildUnitReference(floor, apartment, tower);
  } else if (!label) {
    label = [tower, floor, apartment].filter(Boolean).join(" - ");
  }

  if (!label) return "";
  if (child.is_rented) return `${label} (alugado)`;
  return label;
}

export function formatChildrenCountLabel(count: number): string {
  if (count === 1) return "Este imóvel possui 1 unidade filha";
  return `Este imóvel possui ${count} unidades filhas`;
}

export function getVisitPropertyFamily(property: unknown): VisitPropertyFamily {
  const empty: VisitPropertyFamily = {
    parentLabel: null,
    childrenCount: 0,
    children: [],
  };

  if (
    property == null ||
    Array.isArray(property) ||
    typeof property !== "object"
  ) {
    return empty;
  }

  const record = property as Record<string, unknown>;
  const rawChildren = Array.isArray(record.children)
    ? (record.children as VisitPropertyChild[])
    : [];

  const children = rawChildren
    .map((child) => {
      const label = formatChildLabel(child);
      if (!label) return null;
      const id =
        child.id != null && Number.isFinite(Number(child.id))
          ? Number(child.id)
          : null;
      return { id, label };
    })
    .filter((child): child is { id: number | null; label: string } => child != null);

  const counted = Number(record.children_count);
  const childrenCount = Number.isFinite(counted)
    ? Math.max(counted, children.length)
    : children.length;

  return {
    parentLabel: formatParentLabel(record.parent, record.parent_property_id),
    childrenCount,
    children,
  };
}
